import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";

import { useApp } from "../../../pages-vue/App/useApp";
import { useDraftsStore } from "../../../pages-vue/MessageCenter/store/useDraftsStore";
import { useUserStore } from "../../../pages-vue/MessageCenter/store/useUsersStore";
import { useConversationStore } from "../../../pages-vue/MessageCenter/store/useConversationStore";

import { MessageService } from "../../../services/message-service";
import { ITypeaheadOptions } from "../../vue-interfaces/i-typeahead";
import { IUsersList } from "../../vue-interfaces/messaging-service/i-user";
import { ConversationTypeEnum } from "../../../enums/ConversationTypeEnum";
import { DraftConvEnum } from "../../../enums/DraftConvEnums";

import TagsInput from "../../vue-custom-elements/TagsInput/TagsInput.vue";

export default defineComponent({
    inject: ["dialogRef"],

    components: {
        TagsInput
    },

    data() {
        return {
            _messageService: null as MessageService,
            users: [] as ITypeaheadOptions[],
            includeChatHistory: false as boolean,
            conversationId: "" as string,
            dialogReference: this.dialogRef as any,
            isLoading: false as boolean,
            chatParticipantLoginIds: [] as string[],
            isGroupConversation: false as boolean,
            isDraftConversation: false as boolean,
            ConversationTypeEnum
        };
    },

    async created() {
        this._messageService = Container.instance.get(MessageService);
    },

    mounted() {
        this.conversationId = this.dialogReference.data.conversationId;
        this.chatParticipantLoginIds = this.dialogReference.data.loginIds;
        this.isGroupConversation = this.dialogReference.data.isGroupConversation;
        this.isDraftConversation = this.dialogReference.data.isDraftConversation;
    },

    computed: {
        ...mapState(useApp, ["loggedInUserDetails"]),
        ...mapState(useUserStore, ["getUserIdByLoginId", "getUser"])
    },

    methods: {
        ...mapActions(useDraftsStore, ["createDraftConversation", "updateDraftConversation"]),
        ...mapActions(useUserStore, ["upsertUsers"]),
        ...mapActions(useConversationStore, ["setActiveConversationId"]),

        async getUsersList(filter: string, limit: number) {
            const userIds = this.users.map((user: any) => user.value);
            const chatUserIds = [];

            this.chatParticipantLoginIds.forEach((id: string) => {
                chatUserIds.push(this.getUserIdByLoginId(id));
            });
            const data = await this._messageService.getUserByName({
                page: 1,
                pageLength: 25,
                term: filter.trim(),
                conversationParticipantUsers: [...chatUserIds, ...userIds]
            });
            if (data.items) {
                let listItems: any = [];
                data.items.forEach((user: IUsersList) => {
                    if (!this.chatParticipantLoginIds.includes(user.loginId)) {
                        listItems.push({
                            name: `${user.firstName} ${user.lastName ? user.lastName : ""}`,
                            value: user.id,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            email: user.emailId,
                            emailId: user.emailId, // duplicating as parameter names are different in common component
                            userId: user.id,
                            loginId: user.loginId,
                            currentStatus: user.currentStatus,
                            profileColor: user.profileColor,
                            profilePicId: user.profilePicId,
                            statusMessage: user.statusMessage
                        });
                    }
                });
                return listItems;
            }
            return [];
        },

        updateTags(value: any) {
            this.users = value;
        },

        async add() {
            try {
                if (this.users.length) {
                    this.isLoading = true;
                    if (this.isGroupConversation && !this.isDraftConversation) {
                        const userIds = this.users.map((user: any) => user.value);
                        await this._messageService.addNewMemberToConv(this.conversationId, {
                            userIds,
                            shareChatHistory: this.includeChatHistory
                        });
                        this.isLoading = false;
                        this.dialogReference.close({ status: "success" });
                    } else if (this.isDraftConversation) {
                        this.upsertUsers(this.users);
                        const draftDetails = {
                            id: this.conversationId,
                            actionType: DraftConvEnum.AddParticipant,
                            users: this.users
                        };
                        this.updateDraftConversation(draftDetails);
                        this.isLoading = false;
                        this.dialogReference.close({ status: "success" });
                    } else {
                        // create group chat from one on one
                        const draftId = `draft-${crypto.randomUUID()}`;
                        this.upsertUsers(this.users);
                        const loginIds = this.users.map((user) => user.loginId);
                        const conversation = {
                            id: draftId,
                            title: null,
                            text: "Draft",
                            unread: 0,
                            modifiedOn: new Date(),
                            loginIds: [...loginIds, this.chatParticipantLoginIds[0]], // add new users and current chat participant
                            users: [...this.users, this.getUser(this.chatParticipantLoginIds[0])],
                            lastMessageId: null,
                            isImportant: false,
                            isCurrentUserTagged: false,
                            conversationType: ConversationTypeEnum.GroupConversation,
                            isDraftConversation: true,
                            isMuted: false
                        };
                        this.createDraftConversation(draftId, conversation, null);
                        this.setActiveConversationId(draftId);
                        this.isLoading = false;
                        this.dialogReference.close({ status: "success" });
                    }
                }
            } catch (error) {
                console.error(error);
            }
        },

        cancel() {
            this.dialogReference.close();
        }
    }
});
